class HoverGiftProgressBar extends HTMLElement {
  constructor() {
    super();
    this.hasAddedGift = false;
    this.debounceTimer = null;
  }

  showLoader() {
    const loader = document.querySelector('[data-cart-loader]');
    if (loader) {
      loader.hidden = false;
    }
  }

  hideLoader() {
    const loader = document.querySelector('[data-cart-loader]');
    if (loader) {
      loader.hidden = true;
    }
  }

  connectedCallback() {
    // Store the gift threshold in a window variable for global access
    window.giftThreshold = parseInt(this.dataset.giftThreshold) || 0;
    
    // Listen for cart changes to update the progress bar
    document.addEventListener('cart:change', this.handleCartChange.bind(this));
    // document.addEventListener('cart:refresh', this.handleCartRefresh.bind(this));
    
    // Listen for clicks on the remove gift button
    document.addEventListener('click', this.handleGlobalClick.bind(this));

    // Check initial state with a slight delay to ensure cart is loaded
    setTimeout(() => this.checkGiftStatus(), 100);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.handleCartChange.bind(this));
    document.removeEventListener('cart:refresh', this.handleCartRefresh.bind(this));
    
    // Clean up the event listener
    document.removeEventListener('click', this.handleGlobalClick.bind(this));

    // Clear any pending timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  handleCartChange(event) {
    if (event.detail && event.detail.cart) {
      if (event.detail.cart.item_count === 0) {
        sessionStorage.removeItem('giftDeclined');
        sessionStorage.removeItem('lastDeclinedGiftVariantId');
        window.giftOperationLock = false; // Reset lock on empty cart
      }
      this.updateProgress(event.detail.cart);
    }
    
    // Skip gift status check if explicitly requested (e.g., when changing size)
    if (event.detail && event.detail.skipGiftCheck) {
      return;
    }
    
    // Debounce gift status check to prevent rapid calls
    this.debouncedCheckGiftStatus();
  }

  async handleCartRefresh() {
    // Fetch fresh cart data and update
    const cart = await this.fetchCart();
    this.updateProgress(cart);
    
    // Debounce gift status check
    this.debouncedCheckGiftStatus();
  }

  debouncedCheckGiftStatus() {
    // Clear any existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Set a new timer to check gift status after a delay
    this.debounceTimer = setTimeout(() => {
      this.checkGiftStatus();
    }, 100);
  }

  updateProgress(cart) {
    const threshold = window.giftThreshold || parseInt(this.dataset.giftThreshold) || 0;
    const cartTotal = cart.total_price;
    
    const progressPercentage = Math.min((cartTotal / threshold) * 100, 100);
    const progressBar = this.querySelector('.hv-gift-progress-bar__bar-inner');
    
    if (progressBar) {
      progressBar.style.width = `${progressPercentage}%`;
    }
    
    // Update text
    const textElement = this.querySelector('.hv-gift-progress-bar__text');
    const isEligible = cartTotal >= threshold;
    
    if (textElement) {
      if (isEligible) {
        textElement.textContent = this.dataset.giftFullText || 'Congratulations! Your gift has been added!';
      } else {
        const remaining = threshold - cartTotal;
        const formattedRemaining = this.formatMoney(remaining);
        const remainingText = (this.dataset.giftRemainingText || 'Only (amount) left to get your free gift!').replace('(amount)', formattedRemaining);
        textElement.textContent = remainingText;
      }
    }
    
    // Update data attributes
    this.dataset.isEligibleForGift = isEligible;
  }

  handleGlobalClick(event) {
    if (event.target.closest('[data-remove-gift-line-item]')) {
      this.handleRemoveGiftClick(event);
    } else if (event.target.closest('[data-put-back-gift-line]')) {
      this.handlePutBackGiftClick(event);
    }
  }

  async handleRemoveGiftClick(event) {
    const removeButton = event.target.closest('[data-remove-gift-line-item]');
    if (!removeButton) {
      return;
    }

    event.preventDefault();

    sessionStorage.setItem('giftDeclined', 'true');

    try {
      const cart = await this.fetchCart();
      const giftItem = cart.items.find(item => 
        item.properties && String(item.properties.gift) === 'true'
      );

      if (giftItem) {
        sessionStorage.setItem('lastDeclinedGiftVariantId', giftItem.variant_id);
        await this.removeGift(giftItem);
      }
    } catch (error) {
      console.error('Error handling gift removal:', error);
    }
  }

  async handlePutBackGiftClick(event) {
    event.preventDefault();
    const variantId = sessionStorage.getItem('lastDeclinedGiftVariantId');
    if (!variantId) {
      return;
    }

    sessionStorage.removeItem('giftDeclined');

    try {
      await this.addGift(variantId);
    } catch (error) {
      console.error('Error re-adding gift to cart:', error);
    }
  }

  async checkGiftStatus() {
    if (window.giftOperationLock) return;
    
    const putBackElement = document.querySelector('[data-put-back-gift-line]');

    if (sessionStorage.getItem('giftDeclined') === 'true') {
      const cart = await this.fetchCart().catch(() => ({ items: [] }));
      const giftItem = cart.items.find(item => item.properties && String(item.properties.gift) === 'true');
      if (giftItem) {
        await this.removeGift(giftItem);
      }

      const threshold = window.giftThreshold || parseInt(this.dataset.giftThreshold) || 0;
      const isEligible = cart.total_price >= threshold;

      if (putBackElement) {
        if (isEligible) {
          putBackElement.style.opacity = '1';
          putBackElement.style.visibility = 'visible';
        } else {
          putBackElement.style.opacity = '0';
          putBackElement.style.visibility = 'hidden';
        }
      }
      
      return;
    }

    if (putBackElement) {
      putBackElement.style.opacity = '0';
      putBackElement.style.visibility = 'hidden';
    }

    // Don't process if already processing
    if (window.giftOperationLock) return;
    
    try {
      // Get current cart
      const cart = await this.fetchCart();
      const threshold = window.giftThreshold || parseInt(this.dataset.giftThreshold) || 0;
      const cartTotal = cart.total_price;
      const isEligible = cartTotal >= threshold;
      
      // Find any gift items in cart
      const giftItem = cart.items.find(item => 
        item.properties && String(item.properties.gift) === 'true'
      );
      
      // If cart total is below threshold and there's a gift, remove it
      if (cartTotal < threshold && giftItem) {
        console.log('Cart total below threshold, removing gift');
        await this.removeGift(giftItem);
        return; // Exit early after removing gift
      }
      
      // Only add gift if eligible AND there's no gift at all in cart
      const variantId = this.dataset.variantToAdd;
      if (isEligible && !giftItem && variantId) {
        console.log('Cart eligible for gift, adding gift');
        await this.addGift(variantId);
      }
      
      // Update flag based on current state
      this.hasAddedGift = !!giftItem;
    } catch (error) {
      console.error('Error checking gift status:', error);
    }
  }

  async addGift(variantId) {
    if (window.giftOperationLock) return;
    window.giftOperationLock = true;
    this.showLoader();
    
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[gift]', 'true');
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to add gift to cart');
      }

      // Always dispatch refresh after successfully adding gift
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
    } catch (error) {
      console.error('Failed to add gift to cart:', error);
    } finally {
      setTimeout(() => {
        window.giftOperationLock = false;
        this.hideLoader();
      }, 500);
    }
  }

  async removeGift(giftItem) {
    if (window.giftOperationLock) return;
    window.giftOperationLock = true;
    this.showLoader();
    
    try {
      const updates = {
        [giftItem.key]: 0 // Set quantity to 0 to remove
      };
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove gift from cart');
      }
      
      // Dispatch cart refresh to update UI
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
    } catch (error) {
      console.error('Failed to remove gift from cart:', error);
    } finally {
      setTimeout(() => {
        window.giftOperationLock = false;
        this.hideLoader();
      }, 500);
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    
    // Fallback to basic formatting
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  }
}

// Register the custom element
if (!window.customElements.get('hover-gift-progress-bar')) {
  window.customElements.define('hover-gift-progress-bar', HoverGiftProgressBar);
}